import React, { useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Button, Card, makeStyles, mergeClasses, Title1, tokens } from '@fluentui/react-components'
import { t } from '../i18n/i18n'
import { Text } from '../components/common/text/Text'
import { Image } from '@fluentui/react-components'
import { api } from '../helpers/apiHelper'
import { BankIdQR } from '../components/bankid/BankIdQR'
import { useAuth } from '../context/AuthContext'
import { Loader } from '../components/common/spinner/Loader'

const useStyles = makeStyles({
   centerColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
   },
   wrapper: {
      position: 'absolute',
      top: '45%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      width: '500px',
      padding: tokens.spacingHorizontalXXXL,
   },
   bankIdCard: {
      height: '600px',
   },
   appVersion: {
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
   },
   msalButton: {
      marginTop: tokens.spacingVerticalXXL,
      textDecoration: 'underline',
   },
})

export const Login = () => {
   const classes = useStyles()
   const [hubSessionId, setHubSessionId] = useState<string>(null)
   const [isLoggingIn, setIsLoggingIn] = useState(false)
   const packageJson = require('../../package.json')
   const appVersion = packageJson.version
   const [ip, setIp] = useState<string | null>(null)

   useEffect(() => {
      ;(async () => {
         try {
            const response = await fetch('https://api.ipify.org?format=json')
            const data = await response.json()
            setIp(data.ip)
         } catch (error) {
            setIp(null)
         }
      })()
   }, [])
   const startAuth = async () => {
      const response = await api().startBankIdAuthAsync(ip)
      const data = response.data
      setHubSessionId(data.sessionId)
   }
   const clearSession = () => setHubSessionId(null)

   // Msal login (for test environment only, not for production use)
   const { instance, accounts } = useMsal()
   const { login } = useAuth()

   const manualLogout = localStorage.getItem('manual_logout') === 'true' // To not auto login directly
   useEffect(() => {
      if (accounts.length > 0 && !manualLogout) {
         const exchangeToken = async () => {
            setIsLoggingIn(true)
            try {
               const msalResponse = await instance.acquireTokenSilent({
                  scopes: ['openid', 'profile', `api://${process.env.REACT_APP_CLIENT_ID}/api.scope`],
                  account: accounts[0],
               })

               await api().exchangeMsalTokenIssueJwt({
                  headers: { Authorization: `Bearer ${msalResponse.accessToken}` },
                  credentials: 'include',
               })

               login('msal')
            } catch (error) {
               console.error('Token exchange failed', error)
               setIsLoggingIn(false)
            }
         }
         exchangeToken()
      }
   }, [accounts, instance, login, manualLogout])

   if (isLoggingIn) return <Loader />

   return (
      <article className={classes.centerColumn}>
         <Image src="365Robust.svg" alt={t('365Robust') + 'logo'} height={80} />
         <Card className={mergeClasses(classes.wrapper, hubSessionId ? classes.bankIdCard : '')}>
            {hubSessionId ? (
               <BankIdQR sessionId={hubSessionId} clearSession={clearSession} />
            ) : (
               <>
                  <Title1 align="center" as="h2">
                     {t('SignIn')}
                  </Title1>

                  <Button
                     onClick={async () => await startAuth()}
                     icon={<Image alt={t('365Robust')} src="BankID_logo_white.svg" height={40} />}
                     size="large"
                     appearance="primary"
                  >
                     {t('ShowQRCode')}
                  </Button>
               </>
            )}
            {/* MSAL Button only visible in Test environment + if BankID session hasn't started */}
            {process.env.REACT_APP_ALLOW_MSAL_LOGIN === 'true' && !hubSessionId && <MsalLogin instance={instance} />}
         </Card>
         <Text className={classes.appVersion} align="center">
            v {appVersion}
         </Text>
      </article>
   )
}

const MsalLogin = ({ instance }: { instance: any }) => {
   const classes = useStyles()
   const startMsalAuth = () => {
      localStorage.removeItem('manual_logout');
      instance.loginRedirect()
   }

   return (
      <Button onClick={startMsalAuth} appearance="transparent" className={classes.msalButton}>
         Logga in med MSAL
      </Button>
   )
}

// Autostart link for mobile devies:
// https://app.bankid.com/?autostarttoken=[TOKEN]&redirect=[RETURNURL]
